// src/pages/Menu/Articulacion/Documents/ProjectsPage.jsx 
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import { regionsData } from "../../../../constants/regiones";
import {
  listProjects,
  createProject,
  updateProject,
  deleteProject,
  PROJECT_STATUSES,
  PROJECT_CATEGORIES,
  CATEGORY_LABELS,
  getEffectiveCategories,
  createProjectCategory,
  deleteProjectCategory,
} from "../../../../services/entityProjectsServices";
import {
  listProjectMinutes,
  uploadProjectMinute,
  deleteProjectMinute,
  updateProjectMinute,
} from "../../../../services/projectMinutesService";
import { Banner } from "../../../../components/common/Banner";
import SectionTips from "../../../../components/common/SectionTips";
import ArrowBack from "../../../../components/common/ArrowBack";
import {
  Plus,
  Search,
  MapPin,
  Trash2,
  Tag,
  ChevronRight,
  Calendar,
  MoreVertical,
  ChevronDown,
  Filter,
  ChevronUp,
  X,
  ArrowLeft,
  Upload,
  Edit,
} from "lucide-react";

/* Indicadores service */
import {
  listIndicators,
  createIndicator,
  updateIndicator,
  deleteIndicator,
} from "../../../../services/indicatorsService";

/* Modal de indicadores */
import IndicatorsProjectModal from "./IndicatorsProjectModal";

/* Modales de proyectos */
import ProjectFormModal from "./ProjectsDetails/ProjectFormModal";
import DeleteProjectModal from "./ProjectsDetails/DeleteProjectModal";
import ProjectMinuteModal from "./ProjectsDetails/ProjectMinuteModal";
import UploadProjectMinuteModal from "./ProjectsDetails/UploadProjectMinuteModal";
import ProjectDetails from "./ProjectsDetails/ProjectDetails";

/* ---------- helpers ---------- */
const norm = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const pretty = (slug = "") =>
  String(slug || "")
    .toLocaleLowerCase("es-CL")
    .replace(/_/g, " ")
    .replace(/(^|\s)(\p{L})/gu, (_, sp, ch) => sp + ch.toLocaleUpperCase("es-CL"));

const csvToArr = (csv = "") =>
  String(csv || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const arrToCsv = (arr = []) =>
  (Array.isArray(arr) ? arr : [])
    .map((s) => String(s).trim())
    .filter(Boolean)
    .join(", ");

/* Estados + estilos (proyectos) */
const STATUS_CFG = {
  publicado: {
    from: "#60A5FA",
    to: "#2563EB",
    text: "text-blue-900",
    pill: "bg-blue-50 border-blue-100",
  },
  en_proceso: {
    from: "#34D399",
    to: "#059669",
    text: "text-emerald-900",
    pill: "bg-emerald-50 border-emerald-100",
  },
  finalizado: {
    from: "#F59E0B",
    to: "#D97706",
    text: "text-amber-900",
    pill: "bg-amber-50 border-amber-100",
  },
  diseño: {
    from: "#C4B5FD",
    to: "#8B5CF6",
    text: "text-violet-900",
    pill: "bg-violet-50 border-violet-100",
  },
  en_pausa: {
    from: "#9CA3AF",
    to: "#6B7280",
    text: "text-gray-900",
    pill: "bg-gray-50 border-gray-200",
  },
  cancelado: {
    from: "#FCA5A5",
    to: "#EF4444",
    text: "text-red-900",
    pill: "bg-red-50 border-red-100",
  },
};
const statusCfg = (s) => STATUS_CFG[s] || STATUS_CFG.publicado;
const EXTRA_STATUSES = ["diseño", "en_pausa", "cancelado"];
const ALL_STATUSES = Array.from(
  new Set([...(PROJECT_STATUSES || []), ...EXTRA_STATUSES])
);
const STATUS_LABELS = {
  diseño: "Diseño",
  en_pausa: "En pausa",
  cancelado: "Cancelado",
};

/* ---- Fechas ---- */
const parseDateToLocal = (dateString) => {
  if (!dateString) return null;
  const clean = String(dateString).split("T")[0];
  const parts = clean.split("-");
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};
const toDate = (v) =>
  v instanceof Date ? v : typeof v === "string" ? parseDateToLocal(v) : null;
const _rmDot = (s) => s.replace(/\.$/, "");
const fmtOnlyDate = (d) => {
  const D = toDate(d);
  return D ? D.toLocaleDateString("es-CL") : "";
};
const fmtDM = (d) => {
  const D = toDate(d);
  return D
    ? _rmDot(
        new Intl.DateTimeFormat("es-CL", {
          day: "numeric",
          month: "short",
        }).format(D)
      )
    : "";
};
const fmtDMY = (d) => {
  const D = toDate(d);
  return D
    ? _rmDot(
        new Intl.DateTimeFormat("es-CL", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }).format(D)
      )
    : "";
};
const fmtMonY = (d) => {
  const D = toDate(d);
  return D
    ? `${_rmDot(
        new Intl.DateTimeFormat("es-CL", { month: "short" }).format(D)
      )} ${D.getFullYear()}`
    : "";
};
const fmtNiceRange = (a, b) => {
  const A = toDate(a),
    B = toDate(b);
  if (!A && !B) return "";
  if (A && !B) return fmtDMY(A);
  if (!A && B) return fmtDMY(B);
  const sameYear = A.getFullYear() === B.getFullYear();
  const sameMonth = sameYear && A.getMonth() === B.getMonth();
  if (sameMonth) return `${A.getDate()}–${B.getDate()} ${fmtMonY(A)}`;
  if (sameYear) return `${fmtDM(A)} – ${fmtDM(B)} ${A.getFullYear()}`;
  return `${fmtDMY(A)} – ${fmtDM(B)}`;
};

function buildLocationChips(selected) {
  const chips = [];

  const baseText = (selected.location || "").trim();

  const locs = Array.isArray(selected.locations) ? selected.locations : [];

  // Chip "principal" (texto libre que ya tienes en location)
  if (baseText) {
    chips.push({
      type: "main",
      label: baseText,
    });
  }

  if (locs.length > 0) {
    // Primera ubicación detallada
    const first = locs[0];
    const firstLabel = [
      first.region,
      first.province,
      first.district,
    ]
      .filter(Boolean)
      .join(", ");

    if (firstLabel) {
      chips.push({
        type: "detail",
        label: firstLabel,
      });
    }

    // Resto en un chip resumido
    if (locs.length > 1) {
      const rest = locs.slice(1);
      const tooltip = rest
        .map((l) =>
          [l.region, l.province, l.district].filter(Boolean).join(", ")
        )
        .join(" • ");

      chips.push({
        type: "more",
        label: `+${rest.length} ubicaciones más`,
        title: tooltip,
      });
    }
  }

  return chips;
}

/* === helpers fecha seguros para inputs === */
const isYMD = (v) => /^\d{4}-\d{2}-\d{2}$/.test(String(v || ""));
const toYMDForInput = (v) => {
  if (!v) return "";
  if (isYMD(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(+d)) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/* UI classes - Paleta RDS */
const input =
  "px-3 py-2 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const btn = "inline-flex items-center gap-2 px-4 py-2 rounded-xl transition-all";
const btnPri = `${btn} bg-[#0488AF] text-white hover:bg-[#037a98] active:translate-y-[0.5px]`;
const btnSec = `${btn} bg-[#0488AF] text-white hover:bg-[#037a98] active:translate-y-[0.5px]`;
const btnGhost = `${btn} border bg-white hover:bg-gray-50`;

/* ---------- Cards & utils ---------- */
function GradientCard({ children, className = "", style }) {
  const hasFlexClasses = className.includes('flex') || className.includes('flex-1');
  const innerClassName = hasFlexClasses ? 'h-full flex flex-col' : '';
  
  return (
    <div
      className={`rounded-2xl p-[2px] ${className}`}
      style={{
        background:
          "linear-gradient(135deg,#E6F0FF 0%,#B8E6FF 50%,#E6FBFF 100%)",
        ...style,
      }}
    >
      <div className={`rounded-2xl bg-white ring-1 ring-black/5 shadow-sm ${innerClassName}`}>
        {children}
      </div>
    </div>
  );
}

/* Banner lateral (lista) */
function SectionBanner({ title, subtitle, rightSlot }) {
  return (
    <div className="rounded-2xl overflow-hidden shadow-[0_18px_40px_-22px_rgba(2,6,23,0.45)] min-h-[170px]">
      <div
        className="px-5 py-4 text-white h-full flex flex-col justify-between"
        style={{
          background: "linear-gradient(120deg,#0c3b66,#0488AF 80%)",
        }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold leading-tight truncate">
              {title}
            </h3>
            {subtitle && (
              <p className="text-xs text-white/95 mt-0.5 truncate">
                {subtitle}
              </p>
            )}
          </div>
          <div className="relative z-20">{rightSlot}</div>
        </div>
      </div>
    </div>
  );
}

/* Dropdown Orden */
function SortDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [ready, setReady] = useState(false);
  const btnRef = useRef(null);
  const [anchor, setAnchor] = useState({ left: 0, top: 0, width: 220 });
  const opts = [
    { v: "name-asc", label: "Nombre (A–Z)" },
    { v: "name-desc", label: "Nombre (Z–A)" },
  ];
  const current = opts.find((o) => o.v === value)?.label ?? "Ordenar";

  const recalc = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    setAnchor({
      left: Math.round(r.left),
      top: Math.round(r.bottom + 6),
      width: Math.round(r.width || 220),
    });
  };
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      recalc();
      setReady(true);
    };
    measure();
    const s = () => measure();
    window.addEventListener("scroll", s, true);
    window.addEventListener("resize", s);
    return () => {
      window.removeEventListener("scroll", s, true);
      window.removeEventListener("resize", s);
    };
  }, [open]);
  const toggle = () =>
    setOpen((o) => {
      if (o) setReady(false);
      return !o;
    });

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="h-9 inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 text-white px-3 text-[13px] hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white/60"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {current}
        <ChevronDown size={16} className="opacity-90" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[1000]" onClick={toggle} />
          <div
            className="fixed z-[1001]"
            style={{
              left: anchor.left,
              top: anchor.top,
              minWidth: anchor.width,
              visibility: ready ? "visible" : "hidden",
            }}
          >
            <div className="rounded-xl border border-slate-200 bg-white text-slate-800 shadow-xl overflow-hidden">
              {opts.map((o) => (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => {
                    onChange?.(o.v);
                    toggle();
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50
                    ${
                      value === o.v
                        ? "bg-slate-100 font-semibold text-slate-900"
                        : "text-slate-800"
                    }`}
                  role="option"
                  aria-selected={value === o.v}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

const hexToRgba = (hex = "#0488AF", alpha = 0.12) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return `rgba(11,74,143,${alpha})`;
  const r = parseInt(m[1], 16),
    g = parseInt(m[2], 16),
    b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${alpha})`;
};
function useClickOutside(ref, onOutside) {
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside?.();
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [ref, onOutside]);
}

/* ---------- Skeleton ---------- */
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-lg bg-gray-100 ${className}`} />
);

/* ---------- LocationFilters ---------- */
function LocationFilters({ filters, setFilters, inputClass }) {
  const REGIONS = regionsData?.Regions || [];

  const provinces = useMemo(() => {
    if (!filters.region) return [];
    const region = REGIONS.find((r) => r.name === filters.region);
    return region?.provinces || [];
  }, [filters.region, REGIONS]);

  const districts = useMemo(() => {
    if (!filters.province || provinces.length === 0) return [];
    const province = provinces.find((p) => p.name === filters.province);
    return province?.districts || [];
  }, [filters.province, provinces]);

  const handleRegionChange = (e) => {
    const region = e.target.value;
    setFilters((f) => ({ ...f, region, province: "", district: "" }));
  };

  const handleProvinceChange = (e) => {
    const province = e.target.value;
    setFilters((f) => ({ ...f, province, district: "" }));
  };

  const handleDistrictChange = (e) => {
    setFilters((f) => ({ ...f, district: e.target.value }));
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
      <div>
        <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide mb-0.5 block">Región</label>
        <select
          value={filters.region}
          onChange={handleRegionChange}
          className={`${inputClass} border-[#0488AF]/20 focus:border-[#0488AF] focus:ring-[#0488AF]/20 w-full h-9 text-sm text-[#09274A]`}
        >
          <option value="">Todas las regiones</option>
          {REGIONS.map((r) => (
            <option key={r.name} value={r.name}>
              {r.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide mb-0.5 block">Provincia</label>
        <select
          value={filters.province}
          onChange={handleProvinceChange}
          disabled={!filters.region}
          className={`${inputClass} border-[#0488AF]/20 focus:border-[#0488AF] focus:ring-[#0488AF]/20 w-full h-9 text-sm text-[#09274A] ${
            !filters.region ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <option value="">Todas las provincias</option>
          {provinces.map((p) => (
            <option key={p.name} value={p.name}>
              {p.name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide mb-0.5 block">Comuna</label>
        <select
          value={filters.district}
          onChange={handleDistrictChange}
          disabled={!filters.province}
          className={`${inputClass} border-[#0488AF]/20 focus:border-[#0488AF] focus:ring-[#0488AF]/20 w-full h-9 text-sm text-[#09274A] ${
            !filters.province ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <option value="">Todas las comunas</option>
          {districts.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

/* ---------- TagsInput ---------- */
function TagsInput({
  value = [],
  onChange,
  placeholder = "Etiquetas… (Enter)",
}) {
  const [t, setT] = useState("");
  const add = () => {
    const v = t.trim().toUpperCase();
    if (!v) return;
    if (!value.some((x) => norm(x) === norm(v))) onChange([...value, v]);
    setT("");
  };
  return (
    <div className="rounded-xl border-2 border-slate-200 p-2 max-h-[200px] overflow-y-auto">
      <div className="flex flex-wrap gap-2">
        {value.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-50 text-slate-800 border border-slate-200"
          >
            <Tag size={14} className="text-slate-500" />
            <span className="max-w-[10rem] truncate">{v}</span>
            <button
              type="button"
              className="px-1 rounded hover:bg-slate-100"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              ×
            </button>
          </span>
        ))}
        <input
          value={t}
          onChange={(e) => setT(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            }
          }}
          className="flex-1 min-w-[140px] outline-none px-2 py-1 text-sm"
          placeholder={value.length ? "" : placeholder}
        />
      </div>
    </div>
  );
}

/* ---------- KPI ---------- */
const composeValue = (card) => {
  const raw = card?.value;
  if (raw === null || raw === undefined) return "—";
  const s = String(raw).trim();
  if (!s) return "—";
  return s;
};
function KpiCard({ card, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useClickOutside(boxRef, () => setOpen(false));
  const color = card.color || "#0488AF";
  return (
    <div
      className="rounded-xl border-2 border-slate-200 p-3 relative bg-white hover:shadow-sm transition-shadow"
      style={{ borderLeft: `4px solid ${color}` }}
    >
      <div className="flex items-start justify-between mb-1.5 gap-2 pr-7">
        <span
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-medium border shrink-0"
          style={{
            color,
            backgroundColor: hexToRgba(color, 0.12),
            borderColor: hexToRgba(color, 0.4),
          }}
        >
          {card.label}
        </span>
        {/* Botón de opciones en posición fija */}
        <div className="absolute top-2 right-2" ref={boxRef}>
          <button
            onClick={() => setOpen((v) => !v)}
            className="p-1 rounded hover:bg-gray-50 shrink-0"
            title="Opciones"
          >
            <MoreVertical size={14} />
          </button>
          {open && (
            <div className="absolute right-0 top-7 z-20 w-36 rounded-lg border bg-white shadow">
              <button
                className="w-full text-left px-3 py-1.5 text-xs font-semibold hover:bg-gray-50 transition-colors"
                onClick={() => {
                  setOpen(false);
                  onEdit(card);
                }}
              >
                Editar
              </button>
              <button
                className="w-full text-left px-3 py-1.5 text-xs font-semibold text-[#C42251] hover:bg-red-50 transition-colors"
                onClick={() => {
                  setOpen(false);
                  onDelete(card);
                }}
              >
                Eliminar
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Fuente: solo etiqueta con vínculo */}
      {card.source_label || card.source_url ? (
        <div className="mb-1.5 text-[10px] text-gray-600">
          <span className="font-medium text-gray-700">Fuente: </span>
          {card.source_url ? (
            <a
              href={
                card.source_url.startsWith("http")
                  ? card.source_url
                  : `https://${card.source_url}`
              }
              className="text-[#0488AF] hover:text-[#037a98] hover:underline"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              {card.source_label || card.source_url}
            </a>
          ) : (
            <span>{card.source_label}</span>
          )}
        </div>
      ) : null}
      
      <div className="mt-1 flex items-center gap-2">
        <div className="text-xl font-semibold text-gray-900 tabular-nums">
          {composeValue(card)}
        </div>
      </div>
      {card.hint && (
        <div className="mt-1.5 text-xs text-gray-600">
          {card.hint}
        </div>
      )}
      {card.periodicity ? (
        <div className="mt-0.5 text-[10px] text-gray-500">
          Periodicidad: {card.periodicity}
        </div>
      ) : null}
      
      {/* Fechas de actualización */}
      <div className="mt-1.5 space-y-0.5">
        {card.last_updated_at ? (
          <div className="text-[10px] text-gray-600 flex items-center gap-1">
            <Calendar className="w-3 h-3 text-gray-400" />
            <span className="font-medium">Fuente actualizada el:</span>
            <span>{fmtOnlyDate(card.last_updated_at)}</span>
          </div>
        ) : null}
        {card.updated_at ? (
          <div className="text-[10px] text-gray-600 flex items-center gap-1">
            <Calendar className="w-3 h-3 text-gray-400" />
            <span className="font-medium">Dato registrado el:</span>
            <span>{fmtOnlyDate(card.updated_at)}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}
function HeaderMenu({ onAdd }) {
  return (
    <button 
      className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors" 
      onClick={onAdd}
    >
      <Plus size={16} /> Nueva tarjeta
    </button>
  );
}

/* Botón Agregar Categoría */
function AddCategoryButton({ onCreateCategory }) {
  return (
    <button 
      className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors whitespace-nowrap" 
      onClick={onCreateCategory}
    >
      <Tag size={18} /> Agregar categoría
    </button>
  );
}

/* ---------- Fila de proyecto ---------- */
function ProjectRow({ p, isActive, onClick }) {
  const cfg = statusCfg(p.status);
  return (
    <button
      onClick={onClick}
      aria-selected={isActive}
      className={`group relative w-full text-left px-4 py-3 flex items-start justify-between
        rounded-xl border-2 transition-all duration-200
        ${
          isActive
            ? "bg-blue-50/70 border-blue-200 shadow-sm ring-1 ring-blue-200"
            : "bg-white hover:bg-slate-50 border-slate-200 hover:shadow-sm"
        }
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0488AF]`}
    >
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-1.5 rounded-l-xl"
        style={{ background: `linear-gradient(180deg,${cfg.from},${cfg.to})` }}
      />
      <div className="min-w-0 pr-3 pl-2">
        <div className="font-semibold flex items-center gap-2">
          <span className="truncate">{p.title || "(Sin título)"}</span>
          {p.status && (
            <span
              className={`px-2 py-0.5 text-[11px] rounded-full border shadow-sm ${cfg.pill} ${cfg.text}`}
            >
              {STATUS_LABELS[p.status] || pretty(p.status)}
            </span>
          )}
        </div>
        <div className="mt-1 text-xs text-gray-600 flex items-center flex-wrap gap-2">
          {(p.startDate || p.endDate) && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/80 border border-gray-200 shadow-sm backdrop-blur-sm text-gray-700 tabular-nums">
              <Calendar size={12} className="opacity-70" />
              {fmtNiceRange(p.startDate, p.endDate)}
            </span>
          )}
          {p.location && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200">
              <MapPin size={12} className="opacity-70" />
              {p.location}
            </span>
          )}
          {(p.updatedAt || p.updated_at) && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700">
              <Calendar size={12} className="opacity-70" />
              <span className="font-medium">Modificado:</span>
              <span>{fmtOnlyDate(p.updatedAt || p.updated_at)}</span>
            </span>
          )}
        </div>
        {p.description && (
          <div className="mt-1 text-sm text-gray-700 line-clamp-2 break-words overflow-hidden">
            {p.description.length > 150 ? `${p.description.substring(0, 150)}...` : p.description}
          </div>
        )}
      </div>
      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" />
    </button>
  );
}

/* ================== Página ================== */
export default function ProjectsPage() {
  const navigate = useNavigate();
  // data
  const [rows, setRows] = useState([]);
  const [minutes, setMinutes] = useState([]);
  const [selected, setSelected] = useState(null);

  // sort
  const [sortBy, setSortBy] = useState("name-asc");

  // filters + ui
  const [filters, setFilters] = useState({
    q: "",
    category: "",
    status: "",
    isPublic: "",
    region: "",
    province: "",
    district: "",
  });
  const [ui, setUi] = useState({
    loading: true,
    err: "",
    minutesLoading: false,
    deletingMinuteId: null,
    savingProject: false,
    uploadingMinute: false,
  });

  // Modales
  const [modals, setModals] = useState({
    projectForm: { open: false, mode: "create" },
    deleteProject: { open: false },
    uploadMinute: { open: false },
  });

  // TAB detalle
  const [detailTab, setDetailTab] = useState("summary");
  const [viewportHeight, setViewportHeight] = useState(() => typeof window !== 'undefined' ? window.innerHeight : 800);
  const [gridColumns, setGridColumns] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth >= 1024) {
      return '48% 52%';
    }
    return '100%';
  });

  // Ajustar altura y grid columns al cambio de zoom y resize (con debounce para evitar múltiples actualizaciones)
  useLayoutEffect(() => {
    // Inicializar grid columns inmediatamente
    if (typeof window !== 'undefined') {
      const columns = window.innerWidth >= 1024 ? '48% 52%' : '100%';
      setGridColumns(columns);
    }
  }, []);

  // Forzar el estilo en el DOM después de cada render para evitar sobrescrituras
  useLayoutEffect(() => {
    if (mainGridRef.current && typeof window !== 'undefined') {
      const columns = window.innerWidth >= 1024 ? '48% 52%' : '100%';
      // Aplicar estilo directamente para forzar la proporción
      mainGridRef.current.style.gridTemplateColumns = columns;
    }
  }, [selected]);

  useEffect(() => {
    let timeoutId;
    const updateDimensions = () => {
      const newHeight = window.innerHeight;
      setViewportHeight((prevHeight) => {
        // Solo actualizar si cambió significativamente (más de 10px) para evitar re-renders innecesarios
        if (Math.abs(prevHeight - newHeight) > 10) {
          return newHeight;
        }
        return prevHeight;
      });
      
      // Actualizar grid columns según el ancho de la pantalla - usar porcentajes para evitar recálculos
      if (window.innerWidth >= 1024) {
        setGridColumns('48% 52%');
      } else {
        setGridColumns('100%');
      }
    };
    
    const debouncedUpdateDimensions = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDimensions, 150);
    };

    window.addEventListener('resize', debouncedUpdateDimensions);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedUpdateDimensions);
    };
  }, []);

  // Ref para el grid principal - forzar proporciones
  const mainGridRef = useRef(null);

  // Popover de ubicaciones
  const [showLocationsPopover, setShowLocationsPopover] = useState(false);
  const locationsButtonRef = useRef(null);
  const locationsPopoverRef = useRef(null);
  const [locationsPopoverPosition, setLocationsPopoverPosition] = useState({ top: 0, left: 0 });
  const closeTimerRef = useRef(null);

  // Abrir popover al pasar el mouse
  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setShowLocationsPopover(true);
  };

  // Cerrar popover al salir del mouse (con delay)
  const handleMouseLeave = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setShowLocationsPopover(false);
    }, 200);
  };

  // Calcular posición del popover
  useLayoutEffect(() => {
    if (showLocationsPopover && locationsButtonRef.current) {
      const rect = locationsButtonRef.current.getBoundingClientRect();
      setLocationsPopoverPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [showLocationsPopover]);

  // Cerrar popover al cambiar de proyecto
  useEffect(() => {
    setShowLocationsPopover(false);
  }, [selected?.id]);

  // Filtros fecha minutas
  const [minuteFilters, setMinuteFilters] = useState({
    dateFrom: "",
    dateTo: "",
  });

  // Filtros colapsables
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [statusLabelsExpanded, setStatusLabelsExpanded] = useState(false);

  // categorías
  const [catKeys, setCatKeys] = useState(PROJECT_CATEGORIES);
  const [catLabels, setCatLabels] = useState(CATEGORY_LABELS);
  const [catLoading, setCatLoading] = useState(false);

  // Indicadores
  const [indLoading, setIndLoading] = useState(true);
  const [indErr, setIndErr] = useState("");
  const [cards, setCards] = useState([]);

  // Modal Indicadores
  const [indModal, setIndModal] = useState({
    open: false,
    mode: "create",
    initialData: {},
    saving: false,
    editingId: null,
    wasSeed: false,
  });

  // forms
  const [form, setForm] = useState({
    title: "",
    category: "",
    status: ALL_STATUSES[0] || PROJECT_STATUSES[0],
    location: "",
    startDate: "",
    endDate: "",
    startDateMonthOnly: false,
    endDateMonthOnly: false,
    description: "",
    tags: [],
    locations: [],
    isPublic: undefined,
  });
  const [minForm, setMinForm] = useState({ title: "", date: "", file: null });
  const fileRef = useRef(null);

  // Modal editar minuta
  const [pmModal, setPmModal] = useState(null);

  // cargar categorías
  useEffect(() => {
    (async () => {
      setCatLoading(true);
      try {
        const { categories, labels } = await getEffectiveCategories();
        if (Array.isArray(categories) && categories.length) {
          setCatKeys(categories);
          setCatLabels(labels || {});
        }
      } catch {
      } finally {
        setCatLoading(false);
      }
    })();
  }, []);

  // indicadores
  const palette = [
    "#7C3AED",
    "#10B981",
    "#06B6D4",
    "#F59E0B",
    "#EF4444",
    "#3B82F6",
  ];
  const buildSeed = () => [
    {
      id: "seed-pib_nacional",
      label: "PIB nacional",
      value: "",
      color: "#3B82F6",
      periodicity: "",
    },
    {
      id: "seed-pib_regional",
      label: "PIB Regional",
      value: "",
      color: "#10B981",
      periodicity: "",
    },
    {
      id: "seed-desocup",
      label: "Tasa de desocupación",
      value: "",
      color: "#06B6D4",
      periodicity: "",
    },
  ];
  useEffect(() => {
    (async () => {
      setIndLoading(true);
      setIndErr("");
      try {
        const data = await listIndicators();
        if (Array.isArray(data) && data.length) {
          const mapped = data.map((c, i) => ({
            ...c,
            color: c.color || palette[i % palette.length],
            last_updated_at: c.last_updated_at || null, // Fecha de actualización de la fuente
            updated_at: c.updated_at || c.updatedAt || null, // Fecha de registro del dato
            periodicity: c.periodicity || "",
          }));
          setCards(
            mapped.sort(
              (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0)
            )
          );
        } else setCards(buildSeed());
      } catch {
        setCards(buildSeed());
      } finally {
        setIndLoading(false);
      }
    })();
  }, []);

  // cargar proyectos (debounce)
  const cleanFilters = (f) => {
    const q = String(f?.q || "").trim();
    const out = {};
    if (q) out.q = q;
    if (f?.category) out.category = f.category;
    if (f?.status) out.status = f.status;
    // isPublic: solo enviar si es "true" o "false" (no enviar si es "")
    if (f?.isPublic && (f.isPublic === "true" || f.isPublic === "false")) {
      out.isPublic = f.isPublic;
    }
    if (f?.region && f.region.trim()) out.region = f.region.trim();
    if (f?.province && f.province.trim()) out.province = f.province.trim();
    if (f?.district && f.district.trim()) out.district = f.district.trim();
    return out;
  };
  const hasProjectInfo = (p) => {
    const hasTitle = Boolean(String(p?.title || "").trim());
    const hasDesc = Boolean(String(p?.description || "").trim());
    const hasLoc = Boolean(String(p?.location || "").trim());
    const hasDates = Boolean(p?.startDate || p?.endDate);
    const tagsCsv = p?.tags || p?.collaborators || "";
    const hasTags = csvToArr(tagsCsv).length > 0;
    return hasTitle || hasDesc || hasLoc || hasDates || hasTags;
  };
  useEffect(() => {
    const t = setTimeout(async () => {
      setUi((u) => ({ ...u, loading: true, err: "" }));
      try {
        const data = await listProjects(cleanFilters(filters));
        const arr = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];
        setRows((arr || []).filter(hasProjectInfo));
      } catch (e) {
        setUi((u) => ({
          ...u,
          err: e?.message || "No se pudieron cargar los proyectos",
        }));
      } finally {
        setUi((u) => ({ ...u, loading: false }));
      }
    }, 250);
    return () => clearTimeout(t);
  }, [filters]);

  // computed
  const filtered = useMemo(() => {
    const n = norm(filters.q);
    return rows.filter((r) => {
      const tagsCsv = r.tags || r.collaborators || "";
      const blob = [
        r.title,
        r.description,
        r.location,
        r.category,
        r.status,
        tagsCsv,
      ]
        .filter(Boolean)
        .join(" ");
      return (
        (!n || norm(blob).includes(n)) &&
        (!filters.category || r.category === filters.category) &&
        (!filters.status || r.status === filters.status)
      );
    });
  }, [rows, filters]);

  // orden por nombre
  const ordered = useMemo(() => {
    const arr = [...filtered];
    const col = new Intl.Collator("es", {
      sensitivity: "base",
      numeric: true,
    });
    arr.sort((a, b) => {
      const A = a?.title || "";
      const B = b?.title || "";
      return sortBy === "name-asc" ? col.compare(A, B) : col.compare(B, A);
    });
    return arr;
  }, [filtered, sortBy]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of ordered) {
      const k = p.category || "sin_categoria";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(p);
    }
    return map;
  }, [ordered]);

  const renderKeys = useMemo(() => {
    const s = new Set(catKeys);
    for (const k of grouped.keys()) s.add(k);
    return Array.from(s);
  }, [catKeys, grouped]);

  // indicadores handlers
  const openNewIndicator = () =>
    setIndModal({
      open: true,
      mode: "create",
      initialData: {
        label: "",
        value: "",
        color: "#0488AF",
        hint: "",
        periodicity: "",
        source_label: "",
        source_url: "",
        last_updated_at: "",
      },
      saving: false,
      editingId: null,
      wasSeed: false,
    });
  const openEditIndicator = (card) => {
    const wasSeed = String(card.id || "").startsWith("seed-");
    setIndModal({
      open: true,
      mode: "edit",
      initialData: {
        ...card,
        value: card.value ?? "",
        color: (card.color || "#0488AF").startsWith("#")
          ? card.color || "#0488AF"
          : `#${card.color}`,
        periodicity: card.periodicity || "",
        last_updated_at: card.last_updated_at || "",
      },
      saving: false,
      editingId: card.id,
      wasSeed,
    });
  };
  const handleSaveIndicator = async (payload) => {
    setIndModal((m) => ({ ...m, saving: true }));
    try {
      if (indModal.mode === "edit" && indModal.editingId) {
        if (indModal.wasSeed) {
          const created = await createIndicator({
            ...payload,
            order_index: cards.length,
          });
          const withTs = {
            ...created,
            // Usar la fecha del payload (fecha manual ingresada por el usuario)
            last_updated_at:
              payload.last_updated_at !== undefined
                ? payload.last_updated_at
                : created?.last_updated_at || null,
            updated_at: created?.updated_at || created?.updatedAt || null, // Fecha de registro
            periodicity:
              payload.periodicity !== undefined
                ? payload.periodicity
                : created?.periodicity || "",
          };
          setCards((prev) =>
            prev.map((c) => (c.id === indModal.editingId ? withTs : c))
          );
        } else {
          const updated = await updateIndicator(indModal.editingId, payload);
          setCards((prev) =>
            prev.map((c) =>
              c.id === indModal.editingId
                ? {
                    ...c,
                    ...updated,
                    // Usar la fecha del payload (fecha manual ingresada por el usuario)
                    last_updated_at:
                      payload.last_updated_at !== undefined
                        ? payload.last_updated_at
                        : updated?.last_updated_at ||
                          c.last_updated_at ||
                          null,
                    updated_at: updated?.updated_at || updated?.updatedAt || c.updated_at || null, // Fecha de registro
                    periodicity:
                      payload.periodicity !== undefined
                        ? payload.periodicity
                        : updated?.periodicity || c.periodicity || "",
                  }
                : c
            )
          );
        }
      } else {
        const created = await createIndicator({
          ...payload,
          order_index: cards.length,
        });
        const withTs = {
          ...created,
          // Usar la fecha del payload (fecha manual ingresada por el usuario)
          last_updated_at:
            payload.last_updated_at || created?.last_updated_at || null,
          updated_at: created?.updated_at || created?.updatedAt || null, // Fecha de registro
          last_updated_origin:
            payload.last_updated_origin ||
            created?.last_updated_origin ||
            null,
          periodicity: payload.periodicity || created?.periodicity || "",
        };
        setCards((prev) => [...prev, withTs]);
      }
      setIndModal({
        open: false,
        mode: "create",
        initialData: {},
        saving: false,
        editingId: null,
        wasSeed: false,
      });
      Swal.fire({
        icon: "success",
        title: "Guardado",
        timer: 900,
        showConfirmButton: false,
      });
    } catch (e) {
      setIndModal((m) => ({ ...m, saving: false }));
      Swal.fire("No se pudo guardar", e?.message || "", "error");
    }
  };
  const deleteCard = async (card) => {
    const { isConfirmed } = await Swal.fire({
      title: "Eliminar indicador",
      text: `¿Eliminar "${card.label}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;
    try {
      if (!card.id?.toString().startsWith("seed-"))
        await deleteIndicator(card.id);
      setCards((prev) => prev.filter((c) => c.id !== card.id));
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        timer: 900,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire("No se pudo eliminar", e?.message || "", "error");
    }
  };

  // minutos
  const refreshMinutes = async (projectId) => {
    setUi((u) => ({ ...u, minutesLoading: true }));
    try {
      const filters = {};
      if (minuteFilters.dateFrom) filters.dateFrom = minuteFilters.dateFrom;
      if (minuteFilters.dateTo) filters.dateTo = minuteFilters.dateTo;
      const list = (await listProjectMinutes(projectId, filters)) || [];
      setMinutes(list);
    } catch {
    } finally {
      setUi((u) => ({ ...u, minutesLoading: false }));
    }
  };

  const openProject = async (p) => {
    setSelected(p);
    setDetailTab("summary");
    setMinuteFilters({ dateFrom: "", dateTo: "" });
    setForm({
      title: p.title || "",
      category: p.category || "",
      status: p.status || ALL_STATUSES[0] || PROJECT_STATUSES[0],
      location: p.location || "",
      startDate: (p.startDate || "").slice(0, 10),
      endDate: (p.endDate || "").slice(0, 10),
      startDateMonthOnly: false,
      endDateMonthOnly: false,
      description: p.description || "",
      tags: csvToArr(p.tags || p.collaborators || ""),
      isPublic: p.isPublic,
      locations: (p.locations || []).map((loc) => ({
        region: loc.region || "",
        province: loc.province || "",
        district: loc.district || "",
      })),
    });
    await refreshMinutes(p.id);
  };

  // Recargar minutas cuando cambien filtros de fecha
  useEffect(() => {
    if (selected?.id && detailTab === "minutes") {
      const timer = setTimeout(() => {
        refreshMinutes(selected.id);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [minuteFilters.dateFrom, minuteFilters.dateTo, selected?.id, detailTab]);

  const openCreateModal = () => {
    setSelected(null);
    setForm({
      title: "",
      category: "",
      status: ALL_STATUSES[0] || PROJECT_STATUSES[0],
      location: "",
      startDate: "",
      endDate: "",
      startDateMonthOnly: false,
      endDateMonthOnly: false,
      description: "",
      tags: [],
      isPublic: undefined,
      locations: [],
    });
    setModals((m) => ({
      ...m,
      projectForm: { open: true, mode: "create" },
    }));
  };

  const openEditModal = () => {
    setModals((m) => ({
      ...m,
      projectForm: { open: true, mode: "edit" },
    }));
  };

  const openDeleteModal = () => {
    setModals((m) => ({ ...m, deleteProject: { open: true } }));
  };

  const openUploadMinuteModal = () => {
    setMinForm({ title: "", date: "", file: null });
    if (fileRef.current) fileRef.current.value = "";
    setModals((m) => ({ ...m, uploadMinute: { open: true } }));
  };

  const slugify = (s = "") =>
    String(s)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 60);

  async function openCreateCategory() {
    const { value: vals } = await Swal.fire({
      title: "Nueva categoría",
      html: `<div style="display:flex;flex-direction:column;gap:8px;text-align:left">
              <label style="font-size:12px;color:#374151">Nombre visible</label>
              <input id="cat-label" class="swal2-input" style="margin:0" placeholder="Ej: Energía solar">
            </div>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Crear",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const label =
          (document.getElementById("cat-label")?.value || "").trim();
        if (!label) {
          Swal.showValidationMessage("El nombre visible es obligatorio");
          return;
        }
        const key = slugify(label);
        if (!key) {
          Swal.showValidationMessage(
            "No se pudo generar una clave válida"
          );
          return;
        }
        return { label, key };
      },
    });
    if (!vals) return;
    const { key, label } = vals;
    if (catKeys.includes(key))
      return Swal.fire(
        "Ya existe",
        `La clave "${key}" ya está en uso.`,
        "warning"
      );
    try {
      const created = await createProjectCategory({
        key,
        label,
        order_index: catKeys.length,
        is_active: true,
      });
      setCatKeys((ks) => [...ks, created.key || key]);
      setCatLabels((ls) => ({
        ...ls,
        [created.key || key]: created.label || label,
      }));
      Swal.fire({
        icon: "success",
        title: "Categoría creada",
        timer: 900,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire("No se pudo crear", e?.message || "", "error");
    }
  }

  async function openDeleteCategory() {
    const { value: catKey } = await Swal.fire({
      title: "Eliminar categoría",
      html: `<div style="text-align:left">
              <label style="font-size:12px;color:#374151;display:block;margin-bottom:8px">Selecciona la categoría a eliminar</label>
              <select id="cat-select" class="swal2-select" style="width:100%;margin:0">
                <option value="">Seleccionar...</option>
                ${catKeys
                  .map(
                    (k) =>
                      `<option value="${k}">${
                        catLabels[k] || pretty(k)
                      }</option>`
                  )
                  .join("")}
              </select>
            </div>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      preConfirm: () => {
        const key = document.getElementById("cat-select")?.value;
        if (!key) {
          Swal.showValidationMessage("Selecciona una categoría");
          return;
        }
        return key;
      },
    });
    if (!catKey) return;
    const catLabel = catLabels[catKey] || pretty(catKey);
    const { isConfirmed } = await Swal.fire({
      title: "Confirmar eliminación",
      html: `<div>¿Eliminar la categoría <strong>"${catLabel}"</strong>?</div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;
    try {
      await deleteProjectCategory(catKey);
      setCatKeys((ks) => ks.filter((k) => k !== catKey));
      setCatLabels((ls) => {
        const next = { ...ls };
        delete next[catKey];
        return next;
      });
      Swal.fire({
        icon: "success",
        title: "Categoría eliminada",
        timer: 900,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire("No se pudo eliminar", e?.message || "", "error");
    }
  }

  // upload/editar/eliminar minuta
  const performUpload = async () => {
    const { title, date, file } = minForm;
    if (!file) return Swal.fire("Archivo requerido", "Selecciona un PDF.", "info");
    if (file.type !== "application/pdf")
      return Swal.fire("Formato inválido", "Debes subir un PDF", "info");
    if (!title.trim())
      return Swal.fire(
        "Falta título",
        "Ingresa un título para la minuta",
        "info"
      );
    
    // Validar que la fecha no sea futura (solo fecha actual hacia atrás)
    if (date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      // Normalizar la fecha seleccionada a medianoche local
      const selectedDate = new Date(date + 'T00:00:00');
      selectedDate.setHours(0, 0, 0, 0);
      // Comparar solo las fechas (sin hora)
      if (selectedDate > today) {
        await Swal.fire({
          icon: "warning",
          title: "Fecha inválida",
          text: "No se puede subir una minuta con fecha futura. Solo se permiten fechas de hoy hacia atrás.",
        });
        return;
      }
    }
    
    try {
      setUi((u) => ({ ...u, uploadingMinute: true }));
      Swal.fire({
        title: "Subiendo…",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
      await uploadProjectMinute(selected.id, {
        title: title.trim(),
        date: date || undefined,
        file,
      });
      await refreshMinutes(selected.id);
      setMinForm({ title: "", date: "", file: null });
      if (fileRef.current) fileRef.current.value = "";
      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Minuta subida",
        timer: 1100,
        showConfirmButton: false,
      });
      setModals((m) => ({ ...m, uploadMinute: { open: false } }));
    } catch (e) {
      Swal.close();
      Swal.fire("No se pudo subir", e?.message || "", "error");
    } finally {
      setUi((u) => ({ ...u, uploadingMinute: false }));
    }
  };

  const onEditMinute = (m) => {
    setPmModal({ values: m });
  };

  const onDeleteMinute = async (m) => {
    const { isConfirmed } = await Swal.fire({
      title: "¿Eliminar minuta?",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });
    if (!isConfirmed) return;
    try {
      setUi((u) => ({ ...u, deletingMinuteId: m.id }));
      Swal.fire({
        title: "Eliminando…",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => Swal.showLoading(),
      });
      await deleteProjectMinute(selected.id, m.id);
      setMinutes((prev) => prev.filter((x) => x.id !== m.id));
      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Eliminada",
        timer: 900,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.close();
      Swal.fire("No se pudo eliminar", e?.message || "", "error");
    } finally {
      setUi((u) => ({ ...u, deletingMinuteId: null }));
    }
  };

  const saveProject = async () => {
    if (!form.title.trim()) return Swal.fire("Falta título", "", "warning");
    const tagsUpper = form.tags
      .map((t) => String(t).toUpperCase().trim())
      .filter(Boolean);
    const validLocations = (form.locations || []).filter(
      (loc) => loc.region && loc.province && loc.district
    );
    const payload = {
      title: form.title.trim(),
      category: form.category || undefined,
      status: form.status,
      location: form.location?.trim() || undefined,
      startDate: form.startDate || undefined,
      endDate: form.endDate || undefined,
      description: form.description?.trim() || undefined,
      collaborators: arrToCsv(tagsUpper),
      locations: validLocations.length > 0 ? validLocations : undefined,
      ...(form.isPublic !== undefined && { isPublic: form.isPublic }),
    };
    try {
      setUi((u) => ({ ...u, savingProject: true }));
      const isCreate = modals.projectForm.mode === "create";
      if (isCreate) {
        const created = await createProject(payload);
        setRows((prev) => [created, ...prev].filter(hasProjectInfo));
        setModals((m) => ({
          ...m,
          projectForm: { open: false, mode: "create" },
        }));
        setSelected(created);
        await refreshMinutes(created.id);
        Swal.fire({
          icon: "success",
          title: "Proyecto creado",
          timer: 900,
          showConfirmButton: false,
        });
      } else {
        const updated = await updateProject(selected.id, payload);
        setRows((prev) =>
          prev
            .map((p) => (p.id === updated.id ? { ...p, ...updated } : p))
            .filter(hasProjectInfo)
        );
        setSelected((prev) => (prev ? { ...prev, ...updated } : prev));
        setModals((m) => ({
          ...m,
          projectForm: { open: false, mode: "edit" },
        }));
        Swal.fire({
          icon: "success",
          title: "Guardado",
          timer: 900,
          showConfirmButton: false,
        });
      }
    } catch (e) {
      Swal.fire("Error al guardar", e?.message || "", "error");
    } finally {
      setUi((u) => ({ ...u, savingProject: false }));
    }
  };

  const onDeleteProject = async () => {
    try {
      await deleteProject(selected.id);
      setModals((m) => ({ ...m, deleteProject: { open: false } }));
      setSelected(null);
      const data = await listProjects(cleanFilters(filters));
      const arr = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : [];
      setRows((arr || []).filter(hasProjectInfo));
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        timer: 900,
        showConfirmButton: false,
      });
    } catch (e) {
      Swal.fire("No se pudo eliminar", e?.message || "", "error");
    }
  };

  return (
    <div className="w-full max-w-[1100px] mx-auto px-4 py-6 space-y-6">
      {/* Banner principal */}
      <div className="relative">
        <Banner title="Proyectos externos" message="Crea y administra proyectos, revisa su estado y registra minutas." />
        
        <SectionTips
          section="la sección de proyectos"
          buttonTitle="¿Cómo usar esta sección?"
          tipsTitle="¿Cómo usar esta sección?"
          tipsList={[
            'El primer recuadro que ves en esta sección es de indicadores, que te servirán para mantenerte informado, estos son independientes de los proyectos y totalmente personalizables, debes crearlos y actualizarlos manualmente y pueden ser de cualquier índole (económicos, sociales, demográficos, etc).',
            'Abajo a la izquierda está la lista de proyectos, puedes registrar proyectos con información detallada como estado, categoría y descripción. A la derecha se despliega su detalle, dónde podrás ver la información en "Resumen" y en "Minutas" puedes revisar y registrar reuniones relacionadas al proyecto.',
            'Los estados se diferencian por color en las tarjetas de los proyectos, puedes revisar que significa cada color haciendo clic en "Etiqueta de estados".',
            'Haciendo clic en "Filtros" de desplegarán los diferentes filtros para buscar proyectos.',
          ]}
          color="#0488AF"
          videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766417290/Video_Tutorial_m%C3%B3dulo_Articulaci%C3%B3n_P-P_-_Proyectos_Externos_eoqfxd.mp4"
          manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412940/Manual_de_usuario_m%C3%B3dulo_de_articulaci%C3%B3n_P-P_nfoqgo.pdf"
        />
        <div className="px-1 pb-2">
          <ArrowBack />
        </div>
      </div>

      {/* Indicadores */}
      <GradientCard>
        <div className="p-5">
          <div className="flex items-center justify-between gap-2 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Registro de indicadores
            </h3>
            <HeaderMenu onAdd={openNewIndicator} />
          </div>
          {indLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
            </div>
          ) : indErr ? (
            <div className="text-sm text-red-600">{indErr}</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {cards.map((card) => (
                <KpiCard
                  key={card.id}
                  card={card}
                  onEdit={openEditIndicator}
                  onDelete={deleteCard}
                />
              ))}
            </div>
          )}
        </div>
      </GradientCard>

      {/* Banner Proyectos grande con Etiqueta de estados, Botones y Filtros - Ancho completo */}
      <div className="rounded-2xl overflow-hidden shadow-[0_18px_40px_-22px_rgba(2,6,23,0.45)] mb-6">
        {/* Parte superior del banner (azul) con botones */}
        <div
          className="px-5 py-4 text-white"
          style={{
            background: "linear-gradient(120deg,#0c3b66,#0488AF 80%)",
          }}
        >
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="min-w-0">
              <h3 className="text-base font-semibold leading-tight truncate">
                Proyectos
              </h3>
              <p className="text-xs text-white/95 mt-0.5 truncate">
                {`Total de proyectos ${ordered.length > 0 ? `(${ordered.length})` : "(0)"}`}
              </p>
            </div>
            <div className="relative z-20">
              <div className="flex items-center gap-2 text-sm text-white">
                <span>Ordenar</span>
                <SortDropdown value={sortBy} onChange={setSortBy} />
              </div>
            </div>
          </div>
          {/* Botones en el banner */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 text-white text-xs font-semibold transition"
              title="Crear proyecto"
            >
              <Plus size={12} /> Nuevo proyecto
            </button>
            <button
              type="button"
              onClick={openCreateCategory}
              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 text-white text-xs font-semibold transition"
              title="Agregar categoría"
            >
              <Tag size={12} /> Agregar categoría
            </button>
            <button
              type="button"
              onClick={openDeleteCategory}
              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1 bg-white/10 hover:bg-white/15 ring-1 ring-white/20 text-white text-xs font-semibold transition"
              title="Eliminar categoría"
            >
              <Trash2 size={12} /> Eliminar categoría
            </button>
          </div>
        </div>
        
        {/* Parte inferior del banner (blanca con etiqueta de estados y filtros) */}
        <div className="bg-white border-t border-gray-200">
          {/* Etiqueta de estados colapsable */}
          <div 
            className="border-b border-gray-200"
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)"
            }}
          >
            <button
              onClick={() => setStatusLabelsExpanded(!statusLabelsExpanded)}
              className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/50 transition-colors"
              style={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)"
              }}
            >
              <div className="flex items-center gap-1.5">
                <Tag size={16} className="text-[#0488AF]" />
                <span className="text-xs font-semibold text-gray-800">
                  Etiqueta de estados
                </span>
              </div>
              {statusLabelsExpanded ? (
                <ChevronUp size={16} className="text-[#0488AF]" />
              ) : (
                <ChevronDown size={16} className="text-[#0488AF]" />
              )}
            </button>

            {statusLabelsExpanded && (
              <div 
                className="p-3 grid grid-cols-2 sm:grid-cols-3 gap-2 border-t border-gray-200/50"
                style={{
                  background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
                }}
              >
                {Object.entries(STATUS_CFG).map(([status, cfg]) => (
                  <div
                    key={status}
                    className="flex items-center gap-2 min-w-0"
                  >
                    <span
                      className="w-4 h-4 rounded border border-white shadow-sm shrink-0"
                      style={{
                        background: `linear-gradient(180deg,${cfg.from},${cfg.to})`,
                      }}
                    />
                    <span className="truncate text-xs font-semibold text-[#09274A] uppercase tracking-wide">
                      {STATUS_LABELS[status] || pretty(status)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Filtros colapsables */}
          <div 
            className="border-t border-gray-200"
            style={{
              background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)"
            }}
          >
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="w-full px-3 py-2 flex items-center justify-between hover:bg-white/50 transition-colors"
              style={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)"
              }}
            >
              <div className="flex items-center gap-1.5">
                <Filter size={16} className="text-[#0488AF]" />
                <span className="text-xs font-semibold text-gray-800">
                  Filtros
                </span>
                {(filters.status ||
                  filters.category ||
                  filters.isPublic ||
                  filters.region ||
                  filters.province ||
                  filters.district ||
                  filters.q) && (
                  <span 
                    className="px-1.5 py-0.5 rounded-full text-white text-[9px] font-medium"
                    style={{
                      background: "linear-gradient(135deg, #0488AF 0%, #037a98 100%)"
                    }}
                  >
                    Activos
                  </span>
                )}
              </div>
              {filtersExpanded ? (
                <ChevronUp size={16} className="text-[#0488AF]" />
              ) : (
                <ChevronDown size={16} className="text-[#0488AF]" />
              )}
            </button>

            {filtersExpanded && (
              <div 
                className="p-3 space-y-2.5 border-t border-gray-200/50"
                style={{
                  background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(248,250,252,0.95) 100%)"
                }}
              >
                {/* Buscar por título */}
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#0488AF]" />
                  <input
                    className={`h-9 w-full rounded-lg pl-8 pr-8 text-sm text-[#09274A] ${input} placeholder:text-gray-400 border-[#0488AF]/20 focus:border-[#0488AF] focus:ring-[#0488AF]/20`}
                    placeholder="Buscar por título, ubicación, etiqueta…"
                    value={filters.q}
                    onChange={(e) =>
                      setFilters((f) => ({ ...f, q: e.target.value }))
                    }
                  />
                  {filters.q && (
                    <button
                      type="button"
                      onClick={() => setFilters((f) => ({ ...f, q: "" }))}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-gray-100 text-gray-600"
                      aria-label="Limpiar"
                      title="Limpiar"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Grid compacto: Estado, Categoría, Tipo */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide mb-0.5 block">Estado</label>
                    <select
                      value={filters.status}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          status: e.target.value,
                        }))
                      }
                      className={`${input} w-full h-9 text-sm text-[#09274A] border-[#0488AF]/20 focus:border-[#0488AF] focus:ring-[#0488AF]/20`}
                      title="Estado"
                    >
                      <option value="">Todos los estados</option>
                      {ALL_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABELS[s] || pretty(s)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide mb-0.5 block">Categoría</label>
                    <select
                      value={filters.category}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          category: e.target.value,
                        }))
                      }
                      className={`${input} w-full h-9 text-sm text-[#09274A] border-[#0488AF]/20 focus:border-[#0488AF] focus:ring-[#0488AF]/20`}
                      title="Categoría"
                    >
                      <option value="">
                        {catLoading
                          ? "Cargando categorías…"
                          : "Todas las categorías"}
                      </option>
                      {catKeys.map((c) => (
                        <option key={c} value={c}>
                          {catLabels[c] || pretty(c)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#09274A] uppercase tracking-wide mb-0.5 block">Tipo</label>
                    <select
                      value={filters.isPublic}
                      onChange={(e) =>
                        setFilters((f) => ({
                          ...f,
                          isPublic: e.target.value,
                        }))
                      }
                      className={`${input} w-full h-9 text-sm text-[#09274A] border-[#0488AF]/20 focus:border-[#0488AF] focus:ring-[#0488AF]/20`}
                      title="Tipo"
                    >
                      <option value="">Todos</option>
                      <option value="true">Público</option>
                      <option value="false">Privado</option>
                    </select>
                  </div>
                </div>

                {/* ubicación */}
                <LocationFilters
                  filters={filters}
                  setFilters={setFilters}
                  inputClass={input}
                />

                <div className="flex items-center gap-2 pt-1.5 border-t border-gray-200/50">
                  {(filters.status ||
                    filters.category ||
                    filters.isPublic ||
                    filters.region ||
                    filters.province ||
                    filters.district ||
                    filters.q) && (
                    <button
                      onClick={() =>
                        setFilters((f) => ({
                          ...f,
                          status: "",
                          category: "",
                          isPublic: "",
                          region: "",
                          province: "",
                          district: "",
                          q: "",
                        }))
                      }
                      className="inline-flex items-center justify-center h-9 px-4 rounded-full border border-[#DC3465] text-[#DC3465] text-xs font-semibold tracking-wide uppercase hover:bg-[#C42251] hover:text-white transition-colors"
                      title="Limpiar todos los filtros"
                    >
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* GRID principal: lista + detalle como dos ventanas */}
      <div 
        ref={mainGridRef}
        style={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          width: '100%',
          gap: '24px',
          alignItems: 'stretch',
        }}
      >
        {/* LISTA - con scroll independiente */}
        <div 
          className="lg:flex lg:flex-col" 
          style={{ 
            height: `${Math.max(viewportHeight - 140, 400)}px`, 
            maxHeight: `${viewportHeight - 140}px`, 
            minHeight: '400px',
          }}
        >
          {/* Lista de proyectos - con scroll independiente */}
          <GradientCard className="lg:flex-1 lg:min-h-0 lg:flex lg:flex-col lg:overflow-hidden h-full">
            {ui.loading ? (
              <div className="p-4 space-y-2 overflow-y-auto flex-1">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-16 rounded-xl bg-slate-100/80 animate-pulse"
                  />
                ))}
              </div>
            ) : ordered.length === 0 ? (
              <div className="p-8 text-sm text-center text-gray-600 flex-1 flex items-center justify-center">
                <div>
                  {filters.q ? (
                    <>
                      No hay resultados para{" "}
                      <span className="font-semibold text-gray-900">
                        "{filters.q}"
                      </span>
                      .
                      <div className="mt-3">
                        <button
                          onClick={() =>
                            setFilters((f) => ({ ...f, q: "" }))
                          }
                          className="inline-flex items-center gap-2 h-9 px-4 rounded-full border border-[#DC3465] text-[#DC3465] text-xs font-semibold tracking-wide uppercase hover:bg-[#C42251] hover:text-white transition-colors"
                        >
                          <X className="w-4 h-4" /> Limpiar filtro
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-4xl mb-3">📋</div>
                      <p className="font-medium text-gray-900">
                        No hay proyectos
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Crea tu primer proyecto para comenzar
                      </p>
                      <button
                        onClick={openCreateModal}
                        className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-gradient-to-r from-[#09274A] to-[#0488AF] text-white text-sm font-semibold shadow hover:from-[#071f3d] hover:to-[#03779C] transition-colors mt-4"
                      >
                        <Plus className="w-4 h-4" /> Crear primer proyecto
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto p-2">
                <div className="space-y-2">
                  {Array.from(grouped.keys()).map((catKey) => {
                    const items = grouped.get(catKey) || [];
                    if (!items.length) return null;
                    return (
                      <div
                        key={catKey}
                        className="border-t border-slate-200 first:border-t-0"
                      >
                        <div className="px-5 pt-4 pb-2.5 text-xs font-semibold text-gray-800 bg-gradient-to-r from-slate-50 to-blue-50/30 rounded-t-2xl first:rounded-t-none flex items-center justify-between border-b border-slate-200/50">
                          <span>
                            Total proyectos{" "}
                            {catLabels[catKey] ||
                              (catKey === "sin_categoria"
                                ? "Sin categoría"
                                : pretty(catKey))}{" "}
                            ({items.length})
                          </span>
                        </div>
                        <div className="px-5 pb-4 space-y-2.5 bg-white/50">
                          {items.map((p) => (
                            <ProjectRow
                              key={p.id}
                              p={p}
                              isActive={selected?.id === p.id}
                              onClick={() => openProject(p)}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </GradientCard>
        </div>

        {/* Detalle - con scroll independiente */}
        <div className="lg:flex lg:flex-col" style={{ height: `${Math.max(viewportHeight - 140, 400)}px`, maxHeight: `${viewportHeight - 140}px`, minHeight: '400px' }}>
          {!selected ? (
            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm lg:flex-1 lg:min-h-0 flex items-center justify-center h-full">
              <div className="text-center">
                <div className="text-5xl mb-4 flex justify-center">📋</div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Selecciona un proyecto
                </p>
                <p className="text-xs text-gray-600">
                  Elige un proyecto de la lista para ver su información y
                  minutas
                </p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col lg:min-h-0 space-y-0 h-full">
              {/* Banner del detalle - Bajado un poco */}
              <div
                className="relative rounded-2xl overflow-hidden shadow-[0_18px_38px_-18px_rgba(2,6,23,0.55)] lg:shrink-0 max-h-[200px]"
                style={{
                  background: "linear-gradient(120deg,#0c3b66 0%,#0488AF 100%)",
                }}
              >
                <div className="absolute inset-0 bg-black/5 pointer-events-none" />
                <div className="relative px-4 py-1 sm:px-5 sm:py-2 text-white flex flex-col overflow-hidden">
                  {/* Botones de acción en el banner */}
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <button
                      type="button"
                      onClick={() => setSelected(null)}
                      className="hidden sm:inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 bg-[#C42251] hover:bg-[#A01D45] text-white text-xs font-semibold transition-colors"
                      title="Volver a la lista"
                    >
                      <ArrowLeft size={14} /> Volver
                    </button>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={openUploadMinuteModal}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 bg-gradient-to-r from-[#09274A] to-[#0488AF] hover:from-[#071f3d] hover:to-[#03779C] text-white text-xs font-semibold shadow transition-colors"
                        title="Subir minuta"
                      >
                        <Upload size={14} /> Subir minuta
                      </button>
                      <button
                        type="button"
                        onClick={openEditModal}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 bg-gradient-to-r from-[#09274A] to-[#0488AF] hover:from-[#071f3d] hover:to-[#03779C] text-white text-xs font-semibold shadow transition-colors"
                        title="Editar proyecto"
                      >
                        <Edit size={14} /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={openDeleteModal}
                        className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 bg-[#C42251] hover:bg-[#A01D45] text-white text-xs font-semibold transition-colors"
                        title="Eliminar proyecto"
                      >
                        <Trash2 size={14} /> Eliminar
                      </button>
                    </div>
                  </div>

                  {/* Título */}
                  <h2
                    className="text-lg sm:text-xl font-bold leading-tight break-words mt-1 mb-0.5"
                    style={{ textShadow: "0 1px 2px rgba(0,0,0,.3)" }}
                  >
                    {selected.title || "(Sin título)"}
                  </h2>

                  {/* Chips de info inferiores - Rehecho sin absolute */}
                  <div className="mt-auto pt-5 pb-0.5 flex flex-col gap-1.5">
                    {/* Primera fila: Estado y Categoría */}
                    <div className="flex items-center flex-wrap gap-1.5 text-xs">
                      {/* Estado */}
                      {(() => {
                        const cfg = statusCfg(selected.status);
                        return (
                          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 backdrop-blur-sm shrink-0">
                            <span className="text-white text-[11px] whitespace-nowrap">
                              {STATUS_LABELS[selected.status] || pretty(selected.status)}
                            </span>
                          </div>
                        );
                      })()}

                      {/* Categoría */}
                      {selected.category && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 backdrop-blur-sm shrink-0">
                          <span className="text-white text-[11px] whitespace-nowrap">
                            {catLabels[selected.category] || selected.category}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Segunda fila: Fechas y Ubicaciones */}
                    <div className="flex items-center flex-wrap gap-1.5 text-xs">
                      {/* Fechas */}
                      {(selected.startDate || selected.endDate) && (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 backdrop-blur-sm shrink-0">
                          <Calendar size={11} className="opacity-90 shrink-0" />
                          <span className="text-white text-[11px] whitespace-nowrap">
                            {fmtNiceRange(selected.startDate, selected.endDate)}
                          </span>
                        </div>
                      )}

                      {/* Chip compacto de Ubicaciones con popover */}
                      {((selected.location && selected.location.trim()) || 
                        (Array.isArray(selected.locations) && selected.locations.length > 0)) && (
                        <div 
                          className="relative shrink-0"
                          onMouseEnter={handleMouseEnter}
                          onMouseLeave={handleMouseLeave}
                        >
                          <button
                            type="button"
                            ref={locationsButtonRef}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-white/15 backdrop-blur-sm shrink-0 hover:bg-white/20 transition"
                          >
                            <MapPin size={11} className="opacity-90 shrink-0" />
                            <span className="text-white text-[11px] whitespace-nowrap">
                              Ubicaciones
                              {((selected.location && selected.location.trim()) && Array.isArray(selected.locations) && selected.locations.length > 0) 
                                ? ` (${1 + selected.locations.length})`
                                : Array.isArray(selected.locations) && selected.locations.length > 1
                                ? ` (${selected.locations.length})`
                                : ""}
                            </span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Switch Resumen/Minutas - Por debajo de los chips */}
                  <div className="mt-2 flex items-center justify-end">
                    <div className="inline-flex p-0.5 rounded-xl bg-slate-100/70 border border-slate-200">
                      {[
                        { value: "summary", label: "Resumen" },
                        { value: "minutes", label: "Minutas" },
                      ].map((opt) => {
                        const active = detailTab === opt.value;
                        return (
                          <button
                            key={opt.value}
                            onClick={() => setDetailTab(opt.value)}
                            className={[
                              "px-3 h-8 rounded-lg text-xs font-medium transition focus:outline-none",
                              active
                                ? "text-white shadow-md border border-transparent"
                                : "text-slate-700 hover:bg-white/70 border border-transparent",
                            ].join(" ")}
                            style={
                              active
                                ? {
                                    background:
                                      "linear-gradient(90deg,#0c3b66 0%, #0488AF 100%)",
                                  }
                                : undefined
                            }
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Popover de ubicaciones */}
              {showLocationsPopover && typeof document !== 'undefined' && createPortal(
                <div
                  ref={locationsPopoverRef}
                  className="fixed z-[1000] bg-white rounded-lg shadow-xl border border-gray-200 p-4 max-w-sm"
                  style={{
                    top: `${locationsPopoverPosition.top}px`,
                    left: `${locationsPopoverPosition.left}px`,
                  }}
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={16} className="text-[#0488AF]" />
                    <h3 className="text-sm font-semibold text-gray-900">Ubicaciones</h3>
                  </div>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {/* Ubicación general (campo location) */}
                    {selected.location && selected.location.trim() && (
                      <div className="text-xs text-gray-700 p-2 rounded bg-gray-50">
                        {selected.location}
                      </div>
                    )}
                    {/* Todas las ubicaciones del array */}
                    {Array.isArray(selected.locations) && selected.locations.map((loc, idx) => {
                      const locationText = [loc.region, loc.province, loc.district]
                        .filter(Boolean)
                        .join(", ");
                      if (!locationText) return null;
                      return (
                        <div key={idx} className="text-xs text-gray-700 p-2 rounded bg-gray-50">
                          {locationText}
                        </div>
                      );
                    })}
                  </div>
                </div>,
                document.body
              )}

              {/* Contenido del detalle */}
              <div className="lg:flex-1 lg:min-h-0 lg:max-h-full lg:flex lg:flex-col lg:overflow-hidden" style={{ minHeight: 0, height: '100%', maxHeight: '100%' }}>
                <ProjectDetails
                  selected={selected}
                  detailTab={detailTab}
                  setDetailTab={setDetailTab}
                  minutes={minutes}
                  minuteFilters={minuteFilters}
                  setMinuteFilters={setMinuteFilters}
                  ui={ui}
                  onBack={() => setSelected(null)}
                  onUploadMinute={openUploadMinuteModal}
                  onEdit={openEditModal}
                  onDelete={openDeleteModal}
                  onEditMinute={onEditMinute}
                  onDeleteMinute={onDeleteMinute}
                  catLabels={catLabels}
                  input={input}
                  btnGhost={btnGhost}
                  hideBanner={true}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modales */}
      <ProjectFormModal
        open={modals.projectForm.open}
        mode={modals.projectForm.mode}
        form={form}
        setForm={setForm}
        catKeys={catKeys}
        catLabels={catLabels}
        ALL_STATUSES={ALL_STATUSES}
        STATUS_LABELS={STATUS_LABELS}
        inputClass={input}
        pretty={pretty}
        saving={ui.savingProject}
        onClose={() =>
          setModals((m) => ({
            ...m,
            projectForm: { open: false, mode: m.projectForm.mode },
          }))
        }
        onSave={saveProject}
      />

      <DeleteProjectModal
        open={modals.deleteProject.open}
        projectTitle={selected?.title}
        onClose={() =>
          setModals((m) => ({ ...m, deleteProject: { open: false } }))
        }
        onConfirm={onDeleteProject}
      />

      {selected && (
        <UploadProjectMinuteModal
          open={modals.uploadMinute.open}
          minForm={minForm}
          setMinForm={setMinForm}
          uploading={ui.uploadingMinute}
          fileRef={fileRef}
          projectTitle={selected?.title}
          onClose={() =>
            setModals((m) => ({ ...m, uploadMinute: { open: false } }))
          }
          onUpload={performUpload}
        />
      )}

      {pmModal && selected && (
        <ProjectMinuteModal
          open={!!pmModal}
          initial={pmModal.values}
          projectId={selected.id}
          onClose={() => setPmModal(null)}
          onSaved={() => refreshMinutes(selected.id)}
        />
      )}

      <IndicatorsProjectModal
        open={indModal.open}
        mode={indModal.mode}
        initialData={indModal.initialData}
        onClose={() =>
          setIndModal({
            open: false,
            mode: "create",
            initialData: {},
            saving: false,
            editingId: null,
            wasSeed: false,
          })
        }
        onSave={handleSaveIndicator}
        saving={indModal.saving}
      />
    </div>
  );
}
